import dayjs, { Dayjs } from "dayjs";
import { FLOAT_INPUT_REGEX, INT_INPUT_REGEX } from "src/utils/regexPatterns";
import { dayJsToDateIsoString, isValidLocalDateTimeString } from "src/utils/dayjsUtils.ts";
import { isValidDate } from "iso-datestring-validator";

export interface ObjectAttributeFieldDecoding<T> {
	decode: (valueStr: string | null | undefined) => T;
	encode: (value: T) => string | undefined;
	inputAllowed?: (value: string) => boolean;
}

export const textObjectAttributeFieldDecoding: ObjectAttributeFieldDecoding<string> = {
	decode: (valueStr: string | null | undefined) => valueStr ?? "",
	encode: (value: string) => value,
};

export const integerObjectAttributeFieldDecoding: ObjectAttributeFieldDecoding<number | null> = {
	decode: (valueStr: string | null | undefined) => (valueStr == null ? null : parseInt(valueStr)),
	encode: (value: number | null) => value?.toString() ?? undefined,
	inputAllowed: (value: string) => value.length === 0 || value.match(INT_INPUT_REGEX) !== null,
};

export const decimalObjectAttributeFieldDecoding: ObjectAttributeFieldDecoding<number | null> = {
	decode: (valueStr: string | null | undefined) =>
		valueStr == null ? null : parseFloat(valueStr),
	encode: (value: number | null) => value?.toString() ?? "",
	inputAllowed: (value: string) =>
		value.length === 0 || value.match(FLOAT_INPUT_REGEX) !== null,
};

export const checkObjectAttributeFieldDecoding: ObjectAttributeFieldDecoding<boolean> = {
	decode: (valueStr: string | null | undefined) =>
		valueStr == null ? false : valueStr.toLowerCase() === "true" || valueStr === "1",
	encode: (value: boolean) => value.toString(),
};

export const dateObjectAttributeFieldDecoding: ObjectAttributeFieldDecoding<Dayjs | null> = {
	decode: (valueStr: string | null | undefined) => (valueStr == null ? null : dayjs(valueStr)),
	encode: (value: Dayjs | null) => (value === null ? undefined : dayJsToDateIsoString(value)),
	inputAllowed: function (value: string) {
		return value === "" || isValidDate(value);
	},
};

const DATE_TIME_FORMAT = "yyyy-MM-dd HH:mm:ss";

export const dateTimeObjectAttributeFieldDecoding: ObjectAttributeFieldDecoding<Dayjs | null> = {
	decode: (valueStr: string | null | undefined) =>
		valueStr == null ? null : dayjs(valueStr, DATE_TIME_FORMAT),
	encode: (value: Dayjs | null) => (value === null ? undefined : value.format(DATE_TIME_FORMAT)),
	inputAllowed: function (value: string) {
		return value === "" || isValidLocalDateTimeString(value);
	},
};

export const singleSelectionObjectAttributeFieldDecoding: ObjectAttributeFieldDecoding<
	string | undefined
> = {
	decode: (valueStr: string | null | undefined) => {
		if (valueStr == null) return undefined;
		const values = JSON.parse(valueStr) as string[];
		return values[0] ?? undefined;
	},
	encode: (value: string | undefined) => JSON.stringify(value === undefined ? [] : [value]),
};

export const multiSelectionObjectAttributeFieldDecoding: ObjectAttributeFieldDecoding<string[]> = {
	decode: (valueStr: string | null | undefined) => {
		if (valueStr == null) return [];
		return JSON.parse(valueStr) as string[];
	},
	encode: (value: string[]) => JSON.stringify(value),
};
